import { type FC, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Card, CardContent, Grid, TextField, Typography, Button } from '@packages/shared';
import { useQuery } from 'urql';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { Search32 } from '@packages/themes/icons';
import { GetProductSkusDocument } from '@packages/gql/generated/shopping/GetProductSkusDocument';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer';

interface DirectOrderValues {
  articleNumber: string;
}

const messages = defineMessages({
  directOrderHeadline: {
    id: 'DirectOrder.headline',
    defaultMessage: 'Artikelnummer direkt suchen',
  },
  articleNumberLabel: {
    id: 'DirectOrder.articleNumberLabel',
    defaultMessage: 'Artikelnummer',
  },
  articleNumberPlaceholder: {
    id: 'DirectOrder.articleNumberPlaceholder',
    defaultMessage: 'Artikelnummer deines Artikels',
  },
  description: {
    id: 'DirectOrder.description',
    defaultMessage:
      'Schnell und einfach – suche deinen Artikel anhand der Artikelnummer und füge ihn hinzu',
  },
  articleNumberError: {
    id: 'DirectOrder.articleNumber.Error',
    defaultMessage:
      'Wir haben den Artikel leider nicht gefunden, überprüfe deine Eingabe oder nutze die Suchfunktion dafür.',
  },
});

/* GraphQL */ `
  query GetProductSkus($productId: String!, $locale: String!) {
    product(id: $productId, fallbackId: "", locale: $locale) {
      akl
      sku
    }
  }
`;

const schema = z.object({
  articleNumber: z.string().trim().min(1),
});

export const DirectOrder: FC = () => {
  const { formatMessage, locale } = useIntl();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useForm<DirectOrderValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });
  const productId = watch('articleNumber');
  const [{ data, fetching, error }, executeQuery] = useQuery({
    query: GetProductSkusDocument,
    variables: {
      productId,
      locale,
    },
    pause: true,
  });

  const showError: boolean =
    (!fetching && data && data.product === null) ||
    error !== undefined ||
    errors.articleNumber !== undefined;

  useEffect(() => {
    if (data?.product?.sku) {
      setDialogOpen(true);
    }
  }, [data]);

  return (
    <Box sx={{ width: '100%', p: { xs: '0.75rem', md: '0.75rem 0rem' }, bgcolor: 'common.white' }}>
      {data?.product && (
        <MiniDVLayer
          sku={data.product.sku}
          akl={data.product.akl}
          open={dialogOpen}
          quantity={1}
          onClose={() => setDialogOpen(false)}
        />
      )}
      <Typography
        variant="body3"
        component="p"
        sx={{
          color: 'text.darkTransparent',
          my: 1.5,
        }}
        fontWeight="fontWeightSemiBold"
      >
        {formatMessage(messages.directOrderHeadline)}
      </Typography>

      <form onSubmit={handleSubmit(executeQuery)}>
        <Card sx={{ minheight: { md: '6.813rem' } }}>
          <CardContent>
            <Grid container spacing={{ xs: 1, md: 0 }} alignItems="flex-start">
              <Grid container item xs={12} md={6.8} wrap="nowrap" spacing={2}>
                <Grid item xs={12} md={10.3}>
                  <TextField
                    {...register('articleNumber')}
                    label={formatMessage(messages.articleNumberLabel)}
                    placeholder={formatMessage(messages.articleNumberPlaceholder)}
                    helperText={showError && formatMessage(messages.articleNumberError)}
                    error={showError}
                    sx={{ width: '100%', maxWidth: { xs: 'none', md: '23rem' } }}
                  />
                </Grid>
                <Grid
                  item
                  sx={{ marginTop: '1.25rem', alignSelf: 'flex-start', textAlign: 'right' }}
                >
                  <Button
                    type="submit"
                    sx={{
                      width: '2.25rem',
                      height: '2.25rem',
                      mr: { xs: 'none', md: 2 },
                    }}
                    data-testid="direct-order-button"
                  >
                    <Search32 sx={{ '&&': { width: 32, height: 32 } }} />
                  </Button>
                </Grid>
              </Grid>
              <Grid item xs={12} md={5.2} alignSelf="flex-start" mt={{ xs: '0', md: 2 }}>
                <Typography variant="body2">{formatMessage(messages.description)}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
};
